import { cn } from 'src/lib/utils';
import { BetaChartCard, BetaSectionHeading } from 'src/pages/reports/components/beta-charts';
import { BetaChartAreaSkeleton } from 'src/pages/reports/components/beta-charts/beta-chart-skeletons';
import {
  formatCurrency,
  formatPercentChangeLabel,
  formatRatePercent,
  getChangeDirection,
} from './overview-beta-format';

export interface EfficiencyMetric {
  key: string;
  label: string;
  subtitle: string;
  valueLabel: string;
  previousLabel: string;
  change: number;
}

interface OverviewBetaEfficiencyProps {
  metrics: EfficiencyMetric[];
  isLoading?: boolean;
}

function DeltaBadge({ change }: { change: number }) {
  const direction = getChangeDirection(change);
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11.5px] font-bold',
        direction === 'up' && 'bg-[#ECFDF5] text-[#059669]',
        direction === 'down' && 'bg-[#FEF2F2] text-[#DC2626]',
        direction === 'neutral' && 'bg-[#EDEEF2] text-[#5E6278]'
      )}
    >
      {arrow}
      {' '}
      {formatPercentChangeLabel(change)}
    </span>
  );
}

export function OverviewBetaEfficiency({ metrics, isLoading = false }: OverviewBetaEfficiencyProps) {
  return (
    <div>
      <BetaSectionHeading>
        06 — Efficiency & Rate Metrics
      </BetaSectionHeading>
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <BetaChartCard
            key={metric.key}
            title={metric.label}
            subtitle={metric.subtitle}
            className="mb-0"
          >
            {isLoading ? (
              <BetaChartAreaSkeleton className="h-[120px]" />
            ) : (
              <div className="flex flex-col items-center py-2 text-center">
                <div className="text-[26px] font-bold tracking-[-0.8px] text-[#0D0F1A]">
                  {metric.valueLabel}
                </div>
                <div className="mt-1 text-xs text-[#9CA3AF]">
                  This period
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <div>
                    <div className="mb-1 text-[11px] font-semibold text-[#9CA3AF]">
                      Prev period
                    </div>
                    <div className="font-mono text-[13px] font-bold text-[#0D0F1A]">
                      {metric.previousLabel}
                    </div>
                  </div>
                  <div className="h-8 w-px bg-[#EDEEF2]" />
                  <div>
                    <div className="mb-1 text-[11px] font-semibold text-[#9CA3AF]">
                      Change
                    </div>
                    <DeltaBadge change={metric.change} />
                  </div>
                </div>
              </div>
            )}
          </BetaChartCard>
        ))}
      </div>
    </div>
  );
}

export function buildEfficiencyMetrics(input: {
  ctr: number;
  pastCtr: number;
  cpc: number;
  pastCpc: number;
  cpm: number;
  pastCpm: number;
  cpa: number;
  pastCpa: number;
  ctrChange: number;
  cpcChange: number;
  cpmChange: number;
  cpaChange: number;
}): EfficiencyMetric[] {
  return [
    {
      key: 'ctr',
      label: 'Click-Through Rate',
      subtitle: 'Clicks ÷ impressions',
      valueLabel: formatRatePercent(input.ctr, 3),
      previousLabel: formatRatePercent(input.pastCtr, 3),
      change: input.ctrChange,
    },
    {
      key: 'cpc',
      label: 'Cost Per Click',
      subtitle: 'Spend ÷ clicks',
      valueLabel: formatCurrency(input.cpc),
      previousLabel: formatCurrency(input.pastCpc),
      change: input.cpcChange,
    },
    {
      key: 'cpm',
      label: 'Cost Per Mille',
      subtitle: 'Spend efficiency (CPM)',
      valueLabel: formatCurrency(input.cpm),
      previousLabel: formatCurrency(input.pastCpm),
      change: input.cpmChange,
    },
    {
      key: 'cpa',
      label: 'Cost Per Acquisition',
      subtitle: 'Spend ÷ conversions',
      valueLabel: formatCurrency(input.cpa),
      previousLabel: formatCurrency(input.pastCpa),
      change: input.cpaChange,
    },
  ];
}
